import type { Schemas } from '@argzombie/api-client';
import { Fieldset, IssueList, ItemPicker, ListEditor, NumberInput, TextField } from './fields';
import type { FormProps } from './types';

type Table = Schemas['LootTableDef'];

export function LootTableForm({ value: t, onChange, errors }: FormProps<Table>) {
  const entries = t.entries ?? [];
  const total = entries.reduce((s, e) => s + Math.max(0, e.weight ?? 0), 0);
  const invalid = (p: string) => !!errors[p]?.some((i) => i.severity === 'error');

  return (
    <>
      <Fieldset legend="Loot table">
        <TextField
          label="Id"
          mono
          value={t.id}
          issues={errors.id}
          onChange={(v) => onChange({ ...t, id: v })}
        />
        <p className="self-end text-xs text-bruise">
          Each roll picks one entry by weight, then a quantity in [min, max]. Weight 0 disables an
          entry.
        </p>
      </Fieldset>
      <Fieldset legend="Entries">
        <ListEditor
          label={`Entries (total weight ${total})`}
          hint="Columns: item, weight, min, max, chance per roll."
          what="entry"
          items={entries}
          issues={errors.entries}
          onChange={(next) => onChange({ ...t, entries: next })}
          make={() => ({ itemId: '', weight: 10, min: 1, max: 1 })}
          row={(e, j, set) => (
            <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_4rem_4rem_3.5rem] items-start gap-2">
              <div>
                <ItemPicker
                  label={`Entry ${j + 1} item`}
                  value={e.itemId}
                  invalid={invalid(`entries.${j}.itemId`)}
                  onChange={(v) => set({ ...e, itemId: v ?? '' })}
                />
                <IssueList issues={errors[`entries.${j}.itemId`]} />
              </div>
              <div>
                <NumberInput
                  label={`Entry ${j + 1} weight`}
                  integer
                  min={0}
                  value={e.weight}
                  invalid={invalid(`entries.${j}.weight`)}
                  onChange={(v) => set({ ...e, weight: v ?? 0 })}
                />
                <IssueList issues={errors[`entries.${j}.weight`]} />
              </div>
              <NumberInput
                label={`Entry ${j + 1} min`}
                integer
                min={1}
                value={e.min}
                invalid={invalid(`entries.${j}.max`)}
                onChange={(v) => set({ ...e, min: v ?? 1 })}
              />
              <div>
                <NumberInput
                  label={`Entry ${j + 1} max`}
                  integer
                  min={Math.max(1, e.min ?? 1)}
                  value={e.max}
                  invalid={invalid(`entries.${j}.max`)}
                  onChange={(v) => set({ ...e, max: v ?? 1 })}
                />
                <IssueList issues={errors[`entries.${j}.max`]} />
              </div>
              <span
                className="pt-1.5 text-right font-mono text-xs text-bruise"
                title="Chance per roll"
              >
                {total > 0 ? `${((Math.max(0, e.weight ?? 0) / total) * 100).toFixed(1)}%` : '-'}
              </span>
            </div>
          )}
        />
      </Fieldset>
    </>
  );
}
